// @/components/layout/mobile-header.jsx
// Mobile header with hamburger menu, centered logo, and user avatar dropdown

import React, { useCallback } from "react";
import { useLocation } from "@reach/router";
import { useLayout } from "./main";
import PlatformLogo from "@/assets/logos/PlatformLogo";

// Hooks
import { useContentSidebar } from "@/hooks/content/useContentSidebar";

// Services
import { logoutService } from "@/services/auth/logout";

// V2 reusable components
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  DropdownHeading,
  DropdownDivider,
  DropdownLabel,
  Avatar,
} from "@/components/reusable";

// Icons
import CompanyProfileIcon from "@/assets/icons/ComanyProfileIcon";
import SettingsIcon from "@/assets/icons/SettingsIcon";
import AccessManagementIcon from "@/assets/icons/AccessManagementIcon";
import SignOutIcon from "@/assets/icons/SignOutIcon";
import PrivacyIcon from "@/assets/icons/PrivacyIcon";
import TermsIcon from "@/assets/icons/TermsIcon";
import ChevronIcon from "@/assets/icons/ChevronIcon";

import { BRAND_NAME } from "@/config/brand";
// ============================================================================
// ICONS
// ============================================================================

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-6"
  >
    <line x1="3" y1="8" x2="21" y2="8" />
    <line x1="3" y1="16" x2="21" y2="16" />
  </svg>
);

// ============================================================================
// SIGN OUT BUTTON
// ============================================================================

const SignOutButton = ({ label }) => {
  const handleSignOut = useCallback(async () => {
    try {
      // Use centralized logout service - handles backend logout,
      // cross-tab notification, cache clearing, and navigation
      await logoutService.logout({ reason: 'user_initiated' });
    } catch (error) {
      console.error("Sign out error:", error);
      window.location.href = "/logout/";
    }
  }, []);

  return (
    <DropdownItem onClick={handleSignOut}>
      <SignOutIcon />
      <DropdownLabel>{label}</DropdownLabel>
    </DropdownItem>
  );
};

// ============================================================================
// MOBILE HEADER
// ============================================================================

const MobileHeader = () => {
  const { openMobileMenu } = useLayout();
  const location = useLocation();
  const sidebarContent = useContentSidebar();

  const isPathActive = useCallback(
    (path) => {
      const currentPath = location.pathname;
      if (path === "/") return currentPath === path;
      return currentPath.startsWith(path);
    },
    [location.pathname]
  );

  return (
    <header className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-zinc-200/50 bg-white">
      {/* Hamburger button */}
      <button
        onClick={openMobileMenu}
        className="p-2 -ml-2 rounded-xl text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
        aria-label="Open navigation menu"
      >
        <MenuIcon />
      </button>

      {/* Centered Logo with Dropdown */}
      <Dropdown>
        <DropdownButton
          as="button"
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 -my-1 rounded-xl hover:bg-zinc-100 transition-colors"
        >
          {/* 28px, not the shared 36px: at 36 the wordmark is ~147px wide, 43% of a
              375px screen's usable width, and it is the tallest thing in the header
              so it set the header height too (60px → 52px). */}
          <PlatformLogo className="h-7" />
          <span className="text-zinc-400">
            <ChevronIcon />
          </span>
        </DropdownButton>
        <DropdownMenu className="min-w-48 text-sm" anchor="bottom">
          <DropdownItem href="https://trust.ctrl-s.io/" target="_blank" rel="noopener noreferrer">
            <PrivacyIcon />
            <DropdownLabel>{sidebarContent?.privacyPolicyLabel || "Trust Center"}</DropdownLabel>
          </DropdownItem>
          <DropdownItem href="https://ctrl-s.io/" target="_blank" rel="noopener noreferrer">
            <TermsIcon />
            <DropdownLabel>{sidebarContent?.aboutLabel || `About ${BRAND_NAME}`}</DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {/* User Avatar with Dropdown */}
      <Dropdown>
        <DropdownButton
          as="button"
          className="p-1 -mr-1 rounded-xl hover:bg-zinc-100 transition-colors"
        >
          <Avatar size="sm" />
        </DropdownButton>
        <DropdownMenu className="min-w-64" anchor="bottom end">
          <DropdownSection>
            <DropdownHeading>{sidebarContent?.accountLabel || "Account"}</DropdownHeading>
          </DropdownSection>
          <DropdownItem to="/account/settings/" current={isPathActive("/account/settings/")}>
            <SettingsIcon />
            <DropdownLabel>{sidebarContent?.settingsLabel || "Settings"}</DropdownLabel>
          </DropdownItem>

          <DropdownDivider />

          <DropdownSection>
            <DropdownHeading>{sidebarContent?.companySettingsLabel || "Company Settings"}</DropdownHeading>
          </DropdownSection>
          <DropdownItem to="/account/company-profile/" current={isPathActive("/account/company-profile/")}>
            <CompanyProfileIcon />
            <DropdownLabel>{sidebarContent?.companyProfileLabel || "Company Profile"}</DropdownLabel>
          </DropdownItem>
          <DropdownItem to="/account/users/" current={isPathActive("/account/users/")}>
            <AccessManagementIcon />
            <DropdownLabel>{sidebarContent?.userManagementLabel || "User Management"}</DropdownLabel>
          </DropdownItem>

          <DropdownDivider />
          <SignOutButton label={sidebarContent?.signOutLabel || "Sign out"} />
        </DropdownMenu>
      </Dropdown>
    </header>
  );
};

export default MobileHeader;
